import { i18n_langs, getBrowserLang } from "@/i18n/config";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { getChainsResource } from "@/utils/methods";
import { NET_NAME, TOKEN_TYPE } from "@/config";
import { getSymbol } from "@/config/symbol";

const { urlSuffix }: any = getChainsResource();

const SEOSettings = ({
  title,
  canonicalPath,
  description,
  keywords,
  alternateUrls,
}: any) => (
  <Helmet>
    <title>{title}</title>
    <link rel="canonical" href={`${window.location.origin}${canonicalPath}`} />
    <meta name="description" content={description} />
    <meta name="keywords" content={keywords} />
    {alternateUrls &&
      alternateUrls.map(({ lang, url }: any) => (
        <link rel="alternate" hrefLang={lang} href={`${url}`} key={lang} />
      ))}
    <meta property="og:title" content={title}></meta>
    <meta property="og:type" content="website"></meta>
    <meta
      property="og:url"
      content={`${window.location.origin}${canonicalPath}`}
    ></meta>
    <meta property="og:description" content={description}></meta>
    <meta
      property="og:image"
      content="https://slerf.tools/img/preview/banner.png"
    />
    <meta property="og:image:width" content="1280" />
    <meta property="og:image:height" content="640" />
    <meta
      property="twitter:card"
      content="https://slerf.tools/img/preview/banner.png"
    ></meta>
    <meta property="twitter:site" content="@SlerfTools"></meta>
    <meta property="twitter:title" content={title}></meta>
    <meta property="twitter:description" content={description}></meta>
    <meta
      property="twitter:image"
      content="https://slerf.tools/img/preview/banner.png"
    ></meta>
    <meta property="twitter:image:width" content="1280"></meta>
    <meta property="twitter:image:height" content="640"></meta>
    <meta
      property="twitter:url"
      content={`${window.location.origin}${canonicalPath}`}
    ></meta>
  </Helmet>
);

interface LocalizedUrl {
  lang: string;
  url: string;
}

export function generateLocalizedUrls(
  basePath: string,
  languages: string[] = i18n_langs
): LocalizedUrl[] {
  return languages.map((lang) => ({
    lang: lang,
    url: `/${lang}${basePath}`,
  }));
}

export default function SEOManager() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  // const path = location.pathname;
  // console.log('path',path)
  const seoConfig: any = {
    [`/:lang/${urlSuffix}`]: {
      title: t("index.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/${urlSuffix}`,
      description: t("index.seo.description", { chain: NET_NAME }),
      keywords: t("index.seo.keywords", { chain: NET_NAME }),
      alternateUrls: generateLocalizedUrls(`/${urlSuffix}`),
    },
    [`/:lang/token-creator/${urlSuffix}`]: {
      title: t("tokenCreation.seo.title", {
        chain: "SUI",
        symbol: getSymbol(),
      }),
      canonicalPath: `/:lang/token-creator/${urlSuffix}`,
      description: t("tokenCreation.seo.description", {
        chain: NET_NAME,
        symbol: getSymbol(),
      }),
      keywords: t("tokenCreation.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
      }),
      alternateUrls: generateLocalizedUrls(`/token-creator/${urlSuffix}`),
    },
    [`/:lang/token-management/${urlSuffix}`]: {
      title: t("tokenManagement.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/token-vanity-creator/${urlSuffix}`,
      description: t("tokenManagement.seo.description"),
      keywords: t("tokenManagement.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
      }),
      alternateUrls: generateLocalizedUrls(
        `/token-management/${urlSuffix}`
      ),
    },
    [`/:lang/batch-wallet-generate/${urlSuffix}`]: {
      title: t("walletGenerator.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/batch-wallet-generate/${urlSuffix}`,
      description: t("walletGenerator.seo.description"),
      keywords: t("walletGenerator.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
      }),
      alternateUrls: generateLocalizedUrls(
        `/batch-wallet-generate/${urlSuffix}`
      ),
    },



    [`/:lang/token-vanity-creator/${urlSuffix}`]: {
      title: t("tokenVanityCreation.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/token-vanity-creator/${urlSuffix}`,
      description: t("tokenVanityCreation.seo.description"),
      keywords: t("tokenVanityCreation.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
      }),
      alternateUrls: generateLocalizedUrls(
        `/token-vanity-creator/${urlSuffix}`
      ),
    },
    [`/:lang/create-tax-token/${urlSuffix}`]: {
      title: t("tokenTaxCreation.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/create-tax-token/${urlSuffix}`,
      description: t("tokenTaxCreation.seo.description", { tokenType: TOKEN_TYPE }),
      keywords: t("tokenTaxCreation.seo.keywords", { chain: NET_NAME, tokenType: TOKEN_TYPE }),
      alternateUrls: generateLocalizedUrls(`/create-tax-token/${urlSuffix}`)
    },
    [`/:lang/multi-sender/${urlSuffix}`]: {
      title: t("batchTransfer.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/multi-sender/${urlSuffix}`,
      description: t("batchTransfer.seo.description", {
        chain: NET_NAME,
        tokenType: TOKEN_TYPE,
      }),
      keywords: t("batchTransfer.seo.keywords", { chain: NET_NAME }),
      alternateUrls: generateLocalizedUrls(`/multi-sender/${urlSuffix}`),
    },
    [`/:lang/token-batch-collection/${urlSuffix}`]: {
      title: t("tokenBatchCollection.seo.title", { symbol: getSymbol() }),
      canonicalPath: `/:lang/token-batch-collection/${urlSuffix}`,
      description: t("tokenBatchCollection.seo.description", {
        symbol: getSymbol(),
      }),
      keywords: t("tokenBatchCollection.seo.keywords"),
      alternateUrls: generateLocalizedUrls(
        `/token-batch-collection/${urlSuffix}`
      ),
    },
    // [`/:lang/revoke-authority/${urlSuffix}`]: {
    //   title: t("seo.tokenRevoke.title"),
    //   canonicalPath: `/:lang/revoke-authority/${urlSuffix}`,
    //   description: t("seo.tokenRevoke.description"),
    //   keywords: t("seo.tokenRevoke.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/revoke-authority/${urlSuffix}`)
    // },
    // [`/:lang/token-mint/${urlSuffix}`]: {
    //   title: t("seo.tokenMint.title"),
    //   canonicalPath: `/:lang/token-mint/${urlSuffix}`,
    //   description: t("seo.tokenMint.description"),
    //   keywords: t("seo.tokenMint.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/token-mint/${urlSuffix}`)
    // },
    // [`/:lang/openbook-market/${urlSuffix}`]: {
    //   title: t("seo.lpOpenBook.title"),
    //   canonicalPath: `/:lang/openbook-market/${urlSuffix}`,
    //   description: t("seo.lpOpenBook.description"),
    //   keywords: t("seo.lpOpenBook.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/openbook-market/${urlSuffix}`)
    // },
    // [`/:lang/liquidity-creator/${urlSuffix}`]: {
    //   title: t("seo.lpAdd.title"),
    //   canonicalPath: `/:lang/liquidity-creator/${urlSuffix}`,
    //   description: t("seo.lpAdd.description"),
    //   keywords: t("seo.lpAdd.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/liquidity-creator/${urlSuffix}`)
    // },
    // [`/:lang/liquidity-burner/${urlSuffix}`]: {
    //   title: t("seo.lpBurn.title"),
    //   canonicalPath: `/:lang/liquidity-burner/${urlSuffix}`,
    //   description: t("seo.lpBurn.description"),
    //   keywords: t("seo.lpBurn.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/liquidity-burner/${urlSuffix}`)
    // },
    // [`/:lang/liquidity-remover/${urlSuffix}`]: {
    //   title: t("seo.lpRemove.title"),
    //   canonicalPath: `/:lang/liquidity-remover/${urlSuffix}`,
    //   description: t("seo.lpRemove.description"),
    //   keywords: t("seo.lpRemove.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/liquidity-remover/${urlSuffix}`)
    // },
    [`/:lang/wallet-creator/${urlSuffix}`]: {
      title: t("BatchWalletGeneration.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/wallet-creator/${urlSuffix}`,
      description: t("BatchWalletGeneration.seo.description"),
      keywords: t("BatchWalletGeneration.seo.keywords", { chain: NET_NAME }),
      alternateUrls: generateLocalizedUrls(`/wallet-creator/${urlSuffix}`),
    },
    // [`/:lang/token-clone/${urlSuffix}`]: {
    //   title: t("seo.tokenClone.title"),
    //   canonicalPath: `/:lang/token-clone/${urlSuffix}`,
    //   description: t("seo.tokenClone.description"),
    //   keywords: t("seo.tokenClone.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/token-clone/${urlSuffix}`)
    // },
    // [`/:lang/close-account/${urlSuffix}`]: {
    //   title: t("seo.closeAccount.title"),
    //   canonicalPath: `/:lang/close-account/${urlSuffix}`,
    //   description: t("seo.closeAccount.description"),
    //   keywords: t("seo.closeAccount.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/close-account/${urlSuffix}`)
    // },
    [`/:lang/batch-query-balance/${urlSuffix}`]: {
      title: t("batchQueryBalance.seo.title", {
        chain: NET_NAME,
        symbol: getSymbol(),
      }),
      canonicalPath: `/:lang/batch-query-balance/${urlSuffix}`,
      description: t("batchQueryBalance.seo.description", {
        chain: NET_NAME,
        symbol: getSymbol(),
      }),
      keywords: t("batchQueryBalance.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
      }),
      alternateUrls: generateLocalizedUrls(`/batch-query-balance/${urlSuffix}`),
    },
    [`/:lang/vanity-wallet-generator/${urlSuffix}`]: {
      title: t("prettyAddressCreate.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/vanity-wallet-generator/${urlSuffix}`,
      description: t("prettyAddressCreate.seo.description", {
        chain: NET_NAME,
      }),
      keywords: t("prettyAddressCreate.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
      }),
      alternateUrls: generateLocalizedUrls(
        `/vanity-wallet-generator/${urlSuffix}`
      ),
    },
    [`/:lang/create-liquidity-pool/${urlSuffix}`]: {
      title: t("createLiquidity.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/create-liquidity-pool/${urlSuffix}`,
      description: t("createLiquidity.seo.description", { dex: "PancakeSwap" }),
      keywords: t("createLiquidity.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
        dex: "PancakeSwap",
        tokenType: TOKEN_TYPE,
      }),
      alternateUrls: generateLocalizedUrls(
        `/create-liquidity-pool/${urlSuffix}`
      ),
    },
    [`/:lang/add-liquidity-pool/${urlSuffix}`]: {
      title: t("addLiquidity.seo.title", { chain: NET_NAME, dex: "Cetus" }),
      // title: "gg",
      canonicalPath: `/:lang/create-liquidity-pool/${urlSuffix}`,
      description: t("addLiquidity.seo.description", { chain: NET_NAME, dex: "Cetus" }),
      keywords: t("addLiquidity.seo.keywords", {
        chain: NET_NAME,
        dex: "Cetus",
      }),
      alternateUrls: generateLocalizedUrls(
        `/add-liquidity-pool/${urlSuffix}`
      ),
    },
    [`/:lang/liquidity-create-and-buy-token/${urlSuffix}`]: {
      title: t("lpAddBuy.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/liquidity-create-and-buy-token/${urlSuffix}`,
      description: t("lpAddBuy.seo.description", { chain: NET_NAME }),
      keywords: t("lpAddBuy.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
        dex: "PancakeSwap",
        tokenType: TOKEN_TYPE,
      }),
      alternateUrls: generateLocalizedUrls(
        `/liquidity-create-and-buy-token/${urlSuffix}`
      ),
    },
    [`/:lang/pre-approval/${urlSuffix}`]: {
      title: t("customizedAuthorization.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/pre-approval/${urlSuffix}`,
      description: t("customizedAuthorization.seo.description"),
      keywords: t("customizedAuthorization.seo.keywords"),
      alternateUrls: generateLocalizedUrls(
        `/pre-approval/${urlSuffix}`
      ),
    },
    [`/:lang/mnemonic-wallet-generator/${urlSuffix}`]: {
      title: t("generateFromMnemonic.title"),
      canonicalPath: `/:lang/mnemonic-wallet-generator/${urlSuffix}`,
      description: t("generateFromMnemonic.desc"),
      keywords: t("generateFromMnemonic.keywords"),
      alternateUrls: generateLocalizedUrls(
        `/mnemonic-wallet-generator/${urlSuffix}`
      ),
    },
    // [`/:lang/freeze-account/${urlSuffix}`]: {
    //   title: t("seo.freezeTokenAccount.title"),
    //   canonicalPath: `/:lang/freeze-account/${urlSuffix}`,
    //   description: t("seo.freezeTokenAccount.description"),
    //   keywords: t("seo.freezeTokenAccount.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/freeze-account/${urlSuffix}`)
    // },
    // [`/:lang/multi-collector/${urlSuffix}`]: {
    //   title: t("seo.BatchCollection.title"),
    //   canonicalPath: `/:lang/multi-collector/${urlSuffix}`,
    //   description: t("seo.BatchCollection.description"),
    //   keywords: t("seo.BatchCollection.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/multi-collector/${urlSuffix}`)
    // },
    // [`/:lang/token-vanity-creator/${urlSuffix}`]: {
    //   title: t("seo.PremiumTokenCreation.title"),
    //   canonicalPath: `/:lang/token-vanity-creator/${urlSuffix}`,
    //   description: t("seo.PremiumTokenCreation.description"),
    //   keywords: t("seo.PremiumTokenCreation.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/token-vanity-creator/${urlSuffix}`)
    // },
    // [`/:lang/vanity-address-generator/${urlSuffix}`]: {
    //   title: t("seo.PrettyAddressCreate.title"),
    //   canonicalPath: `/:lang/vanity-address-generator/${urlSuffix}`,
    //   description: t("seo.PrettyAddressCreate.description"),
    //   keywords: t("seo.PrettyAddressCreate.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/vanity-address-generator/${urlSuffix}`)
    // },
    // [`/:lang/token-burner/${urlSuffix}`]: {
    //   title: t("seo.TokenBurn.title"),
    //   canonicalPath: `/:lang/token-burner/${urlSuffix}`,
    //   description: t("seo.TokenBurn.description"),
    //   keywords: t("seo.TokenBurn.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/token-burner/${urlSuffix}`)
    // },
    // [`/:lang/swap/${urlSuffix}`]: {
    //   title: t("seo.Swap.title"),
    //   canonicalPath: `/:lang/swap/${urlSuffix}`,
    //   description: t("seo.Swap.description"),
    //   keywords: t("seo.Swap.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/swap/${urlSuffix}`)
    // },
    // [`/:lang/tax-token-creator/solana`]: {
    //   title: t("seo.token2022Creation.title"),
    //   canonicalPath: `/:lang/tax-token-creator/solana`,
    //   description: t("seo.token2022Creation.description"),
    //   keywords: t("seo.token2022Creation.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/tax-token-creator/${urlSuffix}`)
    // },
    [`/:lang/batch-swap/${urlSuffix}`]: {
      title: t("batchSwap.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/batch-swap/${urlSuffix}`,
      description: t("batchSwap.seo.description"),
      keywords: t("batchSwap.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
      }),
      alternateUrls: generateLocalizedUrls(`/batch-swap/${urlSuffix}`),
    },
    [`/:lang/fourmeme-batch-swap/${urlSuffix}`]: {
      title: t("fourBatchSwap.seo.title", { dex: "FourMeme", chain: NET_NAME }),
      canonicalPath: `/:lang/fourmeme-batch-swap/${urlSuffix}`,
      description: t("fourBatchSwap.seo.description", { chain: NET_NAME }),
      keywords: t("fourBatchSwap.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
        dex: "FourMeme",
      }),
      alternateUrls: generateLocalizedUrls(`/fourmeme-batch-swap/${urlSuffix}`),
    },
    [`/:lang/fourmeme-create-token-and-buy/${urlSuffix}`]: {
      title: t("memeLaunchAndBuyToken.seo.title", { dex: "FourMeme" }),
      canonicalPath: `/:lang/fourmeme-create-token-and-buy/${urlSuffix}`,
      description: t("memeLaunchAndBuyToken.seo.description"),
      keywords: t("memeLaunchAndBuyToken.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
        dex: "FourMeme",
      }),
      alternateUrls: generateLocalizedUrls(
        `/fourmeme-create-token-and-buy/${urlSuffix}`
      ),
    },
    [`/:lang/multisender-many-to-many/${urlSuffix}`]: {
      title: t("manyToMany.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/multisender-many-to-many/${urlSuffix}`,
      description: t("manyToMany.seo.description", { chain: NET_NAME }),
      keywords: t("manyToMany.seo.keywords", { chain: NET_NAME }),
      alternateUrls: generateLocalizedUrls(`/bundled-sell/${urlSuffix}`),
    },
    [`/:lang/fourmeme-bundled-sell/${urlSuffix}`]: {
      title: t("bundledSell.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/fourmeme-bundled-sell/${urlSuffix}`,
      description: t("bundledSell.seo.description"),
      keywords: t("bundledSell.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
        dex: "FourMeme",
      }),
      alternateUrls: generateLocalizedUrls(
        `/fourmeme-bundled-sell/${urlSuffix}`
      ),
    },
    [`/:lang/pancakeswap-bundled-sell/${urlSuffix}`]: {
      title: t("bundledSell.seo.title", { chain: "PancakeSwap" }),
      canonicalPath: `/:lang/pancakeswap-bundled-sell/${urlSuffix}`,
      description: t("bundledSell.seo.description"),
      keywords: t("bundledSell.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
        dex: "PancakeSwap",
      }),
      alternateUrls: generateLocalizedUrls(
        `/pancakeswap-bundled-sell/${urlSuffix}`
      ),
    },
    [`/:lang/fourmeme-bundled-buy/${urlSuffix}`]: {
      title: t("bundledBuy.seo.title", { chain: "FourMeme" }),
      canonicalPath: `/:lang/fourmeme-bundled-buy/${urlSuffix}`,
      description: t("bundledBuy.seo.description"),
      keywords: t("bundledBuy.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
        dex: "FourMeme",
      }),
      alternateUrls: generateLocalizedUrls(`/bundled-buy/${urlSuffix}`),
    },
    [`/:lang/pancakeswap-bundled-buy/${urlSuffix}`]: {
      title: t("bundledBuy.seo.title", { chain: "PancakeSwap" }),
      canonicalPath: `/:lang/pancakeswap-pancakeswap-bundled-buy/${urlSuffix}`,
      description: t("bundledBuy.seo.description"),
      keywords: t("bundledBuy.seo.keywords", {
        chain: NET_NAME,
        symbol: getSymbol(),
        dex: "PancakeSwap",
      }),
      alternateUrls: generateLocalizedUrls(
        `/pancakeswap-pancakeswap-bundled-buy/${urlSuffix}`
      ),
    },
    [`/:lang/fourmeme-anti-mev-bot/${urlSuffix}`]: {
      title: t("antiSandwichTrades.seo.title", { dex: "FourMeme" }),
      canonicalPath: `/:lang/fourmeme-anti-mev-bot/${urlSuffix}`,
      description: t("antiSandwichTrades.seo.description"),
      keywords: t("antiSandwichTrades.seo.keywords", {
        chain: NET_NAME,
        dex: "FourMeme",
      }),
      alternateUrls: generateLocalizedUrls(
        `/fourmeme-anti-mev-bot/${urlSuffix}`
      ),
    },
    [`/:lang/pancakeswap-anti-mev-bot/${urlSuffix}`]: {
      title: t("antiSandwichTrades.seo.title", { dex: "PancakeSwap" }),
      canonicalPath: `/:lang/pancakeswap-anti-mev-bot/${urlSuffix}`,
      description: t("antiSandwichTrades.seo.description"),
      keywords: t("antiSandwichTrades.seo.keywords", {
        chain: NET_NAME,
        dex: "PancakeSwap",
      }),
      alternateUrls: generateLocalizedUrls(
        `/pancakeswap-anti-mev-bot/${urlSuffix}`
      ),
    },
    [`/:lang/approval-checker/${urlSuffix}`]: {
      title: t("walletAuthorizationQuery.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/approval-checker/${urlSuffix}`,
      description: t("walletAuthorizationQuery.seo.description"),
      keywords: t("walletAuthorizationQuery.seo.keywords"),
      alternateUrls: generateLocalizedUrls(`/approval-checker/${urlSuffix}`)
    },
    [`/:lang/claim-liquidity-rewards/${urlSuffix}`]: {
      title: t("liquidityMultifunctional.receiveProfits.seo.title", { chain: NET_NAME, dex: 'PancakeSwap' }),
      canonicalPath: `/:lang/claim-liquidity-rewards/${urlSuffix}`,
      description: t("liquidityMultifunctional.receiveProfits.seo.description", { dex: 'PancakeSwap', chain: NET_NAME }),
      keywords: t("liquidityMultifunctional.receiveProfits.seo.keywords", { chain: NET_NAME, dex: "PancakeSwap" }),
      alternateUrls: generateLocalizedUrls(`/claim-liquidity-rewards/${urlSuffix}`)
    },
    [`/:lang/remove-liquidity-pool/${urlSuffix}`]: {
      title: t("liquidityMultifunctional.remove.seo.title", { chain: NET_NAME, dex: 'PancakeSwap' }),
      canonicalPath: `/:lang/remove-liquidity-pool/${urlSuffix}`,
      description: t("liquidityMultifunctional.remove.seo.description", { chain: NET_NAME, dex: 'PancakeSwap' }),
      keywords: t("liquidityMultifunctional.remove.seo.keywords", { chain: NET_NAME, dex: "PancakeSwap" }),
      alternateUrls: generateLocalizedUrls(`/remove-liquidity-pool/${urlSuffix}`)
    },
    [`/:lang/burn-liquidity-pool/${urlSuffix}`]: {
      title: t("liquidityMultifunctional.burn.seo.title", { chain: NET_NAME, dex: 'PancakeSwap' }),
      canonicalPath: `/:lang/burn-liquidity-pool/${urlSuffix}`,
      description: t("liquidityMultifunctional.burn.seo.description", { dex: 'PancakeSwap' }),
      keywords: t("liquidityMultifunctional.burn.seo.keywords", { chain: NET_NAME, dex: "PancakeSwap" }),
      alternateUrls: generateLocalizedUrls(`/burn-liquidity-pool/${urlSuffix}`)
    },
    [`/:lang/token-snapshot/${urlSuffix}`]: {
      title: t("tokenHolderSnapshot.seo.title", { chain: NET_NAME, symbol: getSymbol() }),
      canonicalPath: `/:lang/token-snapshot/${urlSuffix}`,
      description: t("tokenHolderSnapshot.seo.description", { symbol: getSymbol() }),
      keywords: t("tokenHolderSnapshot.seo.keywords", { chain: NET_NAME, symbol: getSymbol() }),
      alternateUrls: generateLocalizedUrls(`/token-snapshot/${urlSuffix}`)
    },
    [`/:lang/relay-transfer/${urlSuffix}`]: {
      title: t("IntermediaryTransfer.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/relay-transfer/${urlSuffix}`,
      description: t("IntermediaryTransfer.seo.description", { chain: NET_NAME }),
      keywords: t("IntermediaryTransfer.seo.keywords", { chain: NET_NAME, symbol: getSymbol() }),
      alternateUrls: generateLocalizedUrls(`/relay-transfer/${urlSuffix}`)
    },
    [`/:lang/wallet-asset-migrator/${urlSuffix}`]: {
      title: t("walletTransfer.walletMigrationSEO"),
      canonicalPath: `/:lang/wallet-asset-migrator/${urlSuffix}`,
      description: t("walletTransfer.walletMigrationDesc"),
      keywords: t("walletTransfer.walletMigrationKeywords"),
      alternateUrls: generateLocalizedUrls(`/wallet-asset-migrator/${urlSuffix}`)
    },
    [`/:lang/pancakeswap-new-buy/${urlSuffix}`]: {
      title: t("newAddressBuyHolders.seo.title", { dex: "PancakeSwap" }),
      canonicalPath: `/:lang/pancakeswap-new-buy/${urlSuffix}`,
      description: t("newAddressBuyHolders.seo.description", { dex: "PancakeSwap" }),
      keywords: t("newAddressBuyHolders.seo.keywords", { chain: NET_NAME, symbol: getSymbol(), dex: "PancakeSwap" }),
      alternateUrls: generateLocalizedUrls(`/pancakeswap-new-buy/${urlSuffix}`)
    },
    [`/:lang/fourmeme-new-buy/${urlSuffix}`]: {
      title: t("newAddressBuyHolders.seo.title", { dex: "FourMeme" }),
      canonicalPath: `/:lang/fourmeme-new-buy/${urlSuffix}`,
      description: t("newAddressBuyHolders.seo.description", { dex: "FourMeme" }),
      keywords: t("newAddressBuyHolders.seo.keywords", { chain: NET_NAME, symbol: getSymbol(), dex: "FourMeme" }),
      alternateUrls: generateLocalizedUrls(`/fourmeme-new-buy/${urlSuffix}`)
    },
    [`/:lang/batch-pre-approval/${urlSuffix}`]: {
      title: t("batchAuthorization.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/batch-pre-approval/${urlSuffix}`,
      description: t("newAddressBuyHolders.seo.description"),
      keywords: t("newAddressBuyHolders.seo.keywords", { chain: NET_NAME, symbol: getSymbol() }),
      alternateUrls: generateLocalizedUrls(`/batch-pre-approval/${urlSuffix}`)
    },
    [`/:lang/transaction-speedup-cancel/${urlSuffix}`]: {
      title: t("transactionAccelerationAndCancellation.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/transaction-speedup-cancel/${urlSuffix}`,
      description: t("transactionAccelerationAndCancellation.seo.description", { chain: NET_NAME }),
      keywords: t("transactionAccelerationAndCancellation.seo.keywords", { chain: NET_NAME, symbol: getSymbol() }),
      alternateUrls: generateLocalizedUrls(`/transaction-speedup-cancel/${urlSuffix}`)
    },
    [`/:lang/full-token-sell/${urlSuffix}`]: {
      title: t("walletClearance.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/full-token-sell/${urlSuffix}`,
      description: t("walletClearance.seo.description", { chain: NET_NAME }),
      keywords: t("walletClearance.seo.keywords", { chain: NET_NAME, symbol: getSymbol() }),
      alternateUrls: generateLocalizedUrls(`/full-token-sell/${urlSuffix}`)
    },
    [`/:lang/pancakeswap-auto-trade-booster/${urlSuffix}`]: {
      title: t("activeTrade.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/pancakeswap-auto-trade-booster/${urlSuffix}`,
      description: t("activeTrade.seo.description"),
      keywords: t("activeTrade.seo.keywords", { chain: NET_NAME, symbol: getSymbol(), dex: "PancakeSwap" }),
      alternateUrls: generateLocalizedUrls(`/pancakeswap-auto-trade-booster/${urlSuffix}`)
    },
    [`/:lang/fourmeme-auto-trade-booster/${urlSuffix}`]: {
      title: t("activeTrade.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/fourmeme-auto-trade-booster/${urlSuffix}`,
      description: t("activeTrade.seo.description"),
      keywords: t("activeTrade.seo.keywords", { chain: NET_NAME, symbol: getSymbol(), dex: "FourMeme" }),
      alternateUrls: generateLocalizedUrls(`/fourmeme-auto-trade-booster/${urlSuffix}`)
    },
    [`/:lang/rpc-serve/${urlSuffix}`]: {
      title: t("rpcServe.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/rpc-serve/${urlSuffix}`,
      description: t("rpcServe.seo.description", { chain: NET_NAME }),
      keywords: t("rpcServe.seo.keywords", { chain: NET_NAME }),
      alternateUrls: generateLocalizedUrls(`/rpc-serve/${urlSuffix}`)
    },
    [`/:lang/contract-check/${urlSuffix}`]: {
      title: t("contractCheck.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/contract-check/${urlSuffix}`,
      description: t("contractCheck.seo.description", { chain: NET_NAME }),
      keywords: t("contractCheck.seo.keywords", { chain: NET_NAME }),
      alternateUrls: generateLocalizedUrls(`/contract-check/${urlSuffix}`)
    },
    [`/:lang/coin-lock/${urlSuffix}`]: {
      title: t("coinLock.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/coin-lock/${urlSuffix}`,
      description: t("coinLock.seo.description", { chain: NET_NAME }),
      keywords: t("coinLock.seo.keywords", { chain: NET_NAME }),
      alternateUrls: generateLocalizedUrls(`/coin-lock/${urlSuffix}`)
    },
    [`/:lang/coin-lock-list/${urlSuffix}`]: {
      title: t("coinLockList.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/coin-lock-list/${urlSuffix}`,
      description: t("coinLockList.seo.description", { chain: NET_NAME }),
      keywords: t("coinLockList.seo.keywords", { chain: NET_NAME }),
      alternateUrls: generateLocalizedUrls(`/coin-lock-list/${urlSuffix}`)
    },
    [`/:lang/coin-lock-lp/${urlSuffix}`]: {
      title: t("coinLockListLP.seo.title", { chain: NET_NAME }),
      canonicalPath: `/:lang/coin-lock-lp/${urlSuffix}`,
      description: t("coinLockListLP.seo.description", { chain: NET_NAME }),
      keywords: t("coinLockListLP.seo.keywords", { chain: NET_NAME }),
      alternateUrls: generateLocalizedUrls(`/coin-lock-lp/${urlSuffix}`)
    },
    // [`/:lang/pump-volume-bot/${urlSuffix}`]: {
    //   title: t("pumpTrade.seo.title"),
    //   canonicalPath: `/:lang/pump-volume-bot/${urlSuffix}`,
    //   description: t("pumpTrade.seo.description"),
    //   keywords: t("pumpTrade.seo.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/pump-volume-bot/${urlSuffix}`)
    // },
    // [`/:lang/snapshot-token-holders/${urlSuffix}`]: {
    //   title: t("tokenSnapshot.seo.title"),
    //   canonicalPath: `/:lang/snapshot-token-holders/solana`,
    //   description: t("tokenSnapshot.seo.description"),
    //   keywords: t("tokenSnapshot.seo.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/snapshot-token-holders/${urlSuffix}`)
    // },
    // [`/:lang/batch-incinerator/solana`]: {
    //   title: t("batchCloseAccount.seo.title"),
    //   canonicalPath: `/:lang/batch-incinerator/${urlSuffix}`,
    //   description: t("batchCloseAccount.seo.description"),
    //   keywords: t("batchCloseAccount.seo.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/batch-incinerator/${urlSuffix}`)
    // },
    // [`/:lang/token-update/${urlSuffix}`]: {
    //   title: t("TokenUpdate.seo.title"),
    //   canonicalPath: `/:lang/pump-follow-bot/${urlSuffix}`,
    //   description: t("TokenUpdate.seo.description"),
    //   keywords: t("TokenUpdate.seo.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/token-update/${urlSuffix}`)
    // },
    // [`/:lang/auto-freeze-account/${urlSuffix}`]: {
    //   title: t("fullAutomaticFreezeAccount.seo.title"),
    //   canonicalPath: `/:lang/auto-freeze-account/${urlSuffix}`,
    //   description: t("fullAutomaticFreezeAccount.seo.description"),
    //   keywords: t("fullAutomaticFreezeAccount.seo.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/auto-freeze-account/${urlSuffix}`)
    // },
    // [`/:lang/unfreeze-account/${urlSuffix}`]: {
    //   title: t("UnFreezeTokenAccount.seo.title"),
    //   canonicalPath: `/:lang/unfreeze-account/${urlSuffix}`,
    //   description: t("UnFreezeTokenAccount.seo.description"),
    //   keywords: t("UnFreezeTokenAccount.seo.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/unfreeze-account/${urlSuffix}`)
    // },
    // [`/:lang/bundled-sell/${urlSuffix}`]: {
    //   title: t("oneClickSell.seo.title"),
    //   canonicalPath: `/:lang/bundled-sell/${urlSuffix}`,
    //   description: t("oneClickSell.seo.description"),
    //   keywords: t("oneClickSell.seo.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/bundled-sell/${urlSuffix}`)
    // },

    // [`/:lang/maker-buyers/${urlSuffix}`]: {
    //   title: t("MakerBuyers.seo.title"),
    //   canonicalPath: `/:lang/maker-buyers/${urlSuffix}`,
    //   description: t("MakerBuyers.seo.description"),
    //   keywords: t("MakerBuyers.seo.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/maker-buyers/${urlSuffix}`)
    // },
    // [`/:lang/relay-transfer/${urlSuffix}`]: {
    //   title: t("relayTransfer.seo.title"),
    //   canonicalPath: `/:lang/relay-transfer/${urlSuffix}`,
    //   description: t("relayTransfer.seo.description"),
    //   keywords: t("relayTransfer.seo.keywords"),
    //   alternateUrls: generateLocalizedUrls(`/relay-transfer/${urlSuffix}`)
    // }
  };
  // console.log('seoConfig', seoConfig)

  const matchConfig = (path: any) => {
    const formattedPath = path.replace(/\/$/, ""); // Remove trailing slash for uniformity
    const langPattern = new RegExp(`^\/(${i18n_langs.join("|")})`); // Detect language code at the start of the path
    const basePath = formattedPath.replace(langPattern, "/:lang"); // Replace actual lang with placeholder

    const config = seoConfig[basePath];
    if (config) {
      // Replace the ':lang' placeholder with the actual language code from the path
      const actualLang = path.match(langPattern)?.[1] || "en-us"; // Default to 'en-us' if no match
      config.canonicalPath = config.canonicalPath.replace(":lang", actualLang);

      // Update alternate URLs to reflect the full path
      config.alternateUrls = config.alternateUrls.map((link: any) => ({
        ...link,
        url: `${window.location.origin}${link.url.replace(
          ":lang",
          actualLang
        )}`,
      }));
    }
    // console.log('config', config)
    return config;
  };

  const langPattern = new RegExp(`^\/(${i18n_langs.join("|")})`);
  const defaultSEO = {
    title: t("seo.default.title"),
    canonicalPath: location.pathname,
    description: t("seo.default.description"),
    keywords: t("seo.default.keywords"),
    alternateUrls: generateLocalizedUrls(
      location.pathname.replace(langPattern, "")
    ),
  };

  // console.log('defaultSEO', defaultSEO)
  const browserLang = getBrowserLang()?.toLowerCase();
  const clean = location.pathname.toLowerCase().replace(/^\/+/, '');
  const segments = clean.split('/');
  const firstSegment = segments[0];
  let currentPath = location.pathname;
  if (!i18n_langs.includes(firstSegment)) {
    currentPath = `/${browserLang}${location.pathname}`;
  }
  // console.log('currentPath',currentPath)
  const currentSEO = matchConfig(currentPath) || defaultSEO;
  // console.log('currentSEO', currentSEO)
  return <SEOSettings {...currentSEO} />;
}
